/**
 * FieldBlock — `.field-block` from the prototype (cms-redesign-mockup.html
 * ~2920–2935). A labeled slot inside a FormSection.
 *
 *   <FieldBlock label="Title" required hint="Shown on the session card">
 *     <TextInput name="session_name" value={...} onChange={...} />
 *   </FieldBlock>
 *
 * Visual recipe:
 *   • Label: Asap Condensed 11/700 uppercase text-light, 6px below
 *   • `required` → small coral asterisk after the label
 *   • `hint` → inline Asap 11.5 muted text after the label, sentence
 *     case (label is uppercase; the hint deliberately is not)
 *
 * Layout note:
 *   The block is a flex column with the children wrapped in a
 *   `margin-top: auto` div. Standalone (the common case), this
 *   collapses and looks identical to the prototype.
 *
 *   But when two FieldBlocks share a FieldRow and one has a label
 *   that wraps to a second line (long hint, longer label, etc.),
 *   the grid stretches both cells to the tallest one. Without the
 *   auto-margin, the shorter cell's input sits high and the inputs
 *   in the row look misaligned at the bottom. With it, inputs are
 *   pinned to the bottom of each cell and stay flush across the row.
 */
import React from 'react';
import { cn } from '../cn';

export default function FieldBlock({
  label,
  required,
  hint,
  htmlFor,
  children,
  className,
  noMargin,
}) {
  return (
    <div
      className={cn(className)}
      style={{
        marginBottom: noMargin ? 0 : 16,
        minWidth: 0,
        // Flex column so the children wrapper's `marginTop: auto`
        // can push the input to the bottom of a stretched grid
        // cell. See header note above.
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {label && (
        <label
          htmlFor={htmlFor}
          className="block font-condensed font-bold uppercase text-text-light"
          style={{
            fontSize: 11,
            letterSpacing: '0.08em',
            marginBottom: 6,
          }}
        >
          {label}
          {required && (
            <span style={{ color: 'rgb(var(--color-coral))', marginLeft: 4 }}>*</span>
          )}
          {hint && (
            <span
              className="text-text-muted"
              style={{
                fontFamily: 'inherit',
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
                marginLeft: 8,
                fontSize: 11.5,
              }}
            >
              {hint}
            </span>
          )}
        </label>
      )}
      {/* `marginTop: auto` floats the children to the bottom of the
       *  block. Visually a no-op when the block sizes to content
       *  (the common case); only kicks in when a grid cell is
       *  stretched to match a taller sibling. */}
      <div style={{ marginTop: 'auto', minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}
